import React, { memo } from "react";
import {
  Box,
  Group,
  Anchor,
  Image,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { Protocol } from "../api/type";
import { Link } from "../Components/Link";
import { TokenDisplay } from "../Components/TokenDisplay";
import { shrink } from "../Components/Utils";

const PoolDisplay = memo(function ({
  chain,
  tokens,
  address,
  protocol,
  hideIcons,
  ellipsis = "xs",
}: {
  chain: string;
  tokens: Array<{
    address: string;
    decimals: number;
    symbol: string;
  }>;
  address: string;
  protocol?: Protocol;
  hideIcons?: boolean;
  ellipsis?: string;
}) {
  const theme = useMantineTheme();

  const tokenNames = tokens
    .map((token) =>
      token.symbol.length > 10 ? shrink(token.symbol, 6, 4) : token.symbol
    )
    .join("/");
  const tokenFullNames = tokens.map((token) => token.symbol).join("/");

  const maxWidth = 260;

  const lpNames = (
    <Box sx={{ maxWidth: maxWidth }}>
      <Anchor
        component={Link}
        to={`/${chain}/lp/${address}`}
        onClick={(e: any) => {
          e.stopPropagation();
        }}
        sx={(theme) => ({
          fontFamily: '"Consolas", Menlo, monospace',
          color:
            theme.colorScheme === "dark" ? theme.white : theme.colors.dark[6],
          fontWeight: 400,
          fontSize: 14,
        })}
      >
        <Box
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {tokenNames}
        </Box>
      </Anchor>
    </Box>
  );

  return (
    <Box sx={{ whiteSpace: "nowrap" }}>
      {tokens.length ? null : "-"}
      <Group noWrap spacing="xs">
        {hideIcons ? null : (
          <Group noWrap spacing={2}>
            {tokens.map((token, idx) => (
              <TokenDisplay
                key={`token-${idx}`}
                token={{
                  chain,
                  address: token.address,
                  name: token.symbol,
                  symbol: token.symbol,
                }}
                showName={false}
              />
            ))}
          </Group>
        )}
        <Group noWrap spacing="xs" title={tokenFullNames}>
          {lpNames}
          {protocol?.website &&
          protocol?.showName &&
          (protocol.id || protocol.icon) ? (
            <Anchor
              target="_blank"
              href={protocol?.website}
              onClick={(e: any) => {
                e.stopPropagation();
              }}
              sx={{ height: 16, lineHeight: 1 }}
            >
              <Tooltip label={protocol?.showName} withArrow>
                <Image
                  radius={8}
                  sx={{
                    width: 16,
                    height: 16,
                  }}
                  width={16}
                  height={16}
                  styles={{
                    image: {
                      backgroundColor:
                        theme.colorScheme === "dark"
                          ? theme.white
                          : "transparent",
                    },
                  }}
                  alt={protocol.showName}
                  src={
                    protocol.id
                      ? `https://storage.googleapis.com/eigenphi-token-icons/protocols/${protocol.id}.png`
                      : protocol.icon
                          ?.replace(
                            "https://eigenphi.io/images/tokens/protocols/",
                            "https://storage.googleapis.com/eigenphi-token-icons/protocols/"
                          )
                          ?.replace(
                            "/images/tokens/protocols/",
                            "https://storage.googleapis.com/eigenphi-token-icons/protocols/"
                          )
                  }
                />
              </Tooltip>
            </Anchor>
          ) : null}
        </Group>
      </Group>
    </Box>
  );
});

export default PoolDisplay;
